import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Shield, FileText, Download, ExternalLink, CheckCircle, AlertTriangle,
  Workflow, Leaf, Inbox, ClipboardCheck, Thermometer,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DataSourceBadge from '../components/common/DataSourceBadge';
import ChainStatusBadge from '../components/ChainStatusBadge';
import { toast } from 'sonner';
import { buildComplianceReport, downloadJSON, downloadPDF } from '../lib/complianceReport';
import ChainOfCustodyTimeline from '../components/ChainOfCustodyTimeline';
import AuditTrailList from '../components/AuditTrailList';
import BatchRecordingPanel from '../components/BatchRecordingPanel';
import permissions from '../lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Compliance = ({ user }) => {
  const navigate = useNavigate();
  const perms = permissions(user?.role);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [events, setEvents] = useState([]);
  const [monitoringSessions, setMonitoringSessions] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [audit, setAudit] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [bRes, msRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/batches`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/monitoring-sessions/history`).catch(() => ({ data: [] })),
        ]);
        setBatches(bRes.data);
        setMonitoringSessions(msRes.data);
        if (bRes.data?.length > 0) setSelectedBatch(bRes.data[0]);
      } catch (e) {
        // graceful — no toast since empty is expected pre-pilot
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load batch-specific events when a batch is selected
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedBatch) {
        if (!cancelled) {
          setEvents([]);
          setAlarms([]);
          setAudit([]);
          setCorrections([]);
        }
        return;
      }
      const token = localStorage.getItem('mvp_token');
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      try {
        const [evRes, auRes, mcRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/batches/${selectedBatch.id}/events`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/batches/${selectedBatch.id}/audit-trail`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/batches/${selectedBatch.id}/manual-corrections`, cfg).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setEvents(evRes.data || []);
        setAudit(auRes.data || []);
        setCorrections(mcRes.data || []);
      } catch {
        if (cancelled) return;
        setEvents([]); setAudit([]); setCorrections([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBatch]);

  const report = useMemo(
    () =>
      buildComplianceReport({
        batch: selectedBatch,
        events,
        monitoring_sessions: monitoringSessions,
        alarms,
        corrections,
      }),
    [selectedBatch, events, monitoringSessions, alarms, corrections],
  );

  const filename = (ext) =>
    `puresustain-compliance-${selectedBatch?.batch_number || selectedBatch?.id || 'no-batch'}.${ext}`;

  const handleDownloadJSON = () => {
    downloadJSON(report, filename('json'));
    toast.success('JSON report downloaded.');
  };

  const handleDownloadPDF = () => {
    try {
      downloadPDF(report, filename('pdf'));
      toast.success('PDF report downloaded.');
    } catch (e) {
      toast.error('PDF generation failed: ' + e.message);
    }
  };

  const handleOpenPublicQR = () => {
    if (!selectedBatch) {
      toast.info('Public verification will be available once a batch is selected.');
      return;
    }
    window.open(`${window.location.origin}/verify/${selectedBatch.id}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const hasBatch = !!selectedBatch;
  const qrUrl = hasBatch ? `${window.location.origin}/verify/${selectedBatch.id}` : null;

  return (
    <div className="space-y-8 sm:space-y-10 lg:space-y-12" data-testid="compliance-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            Compliance
          </h1>
          <DataSourceBadge
            source={hasBatch ? 'live' : 'demo'}
            label={hasBatch ? 'Live Data' : 'Pending Live Batch Data'}
          />
        </div>
      </div>

      {/* Batch picker (only when batches exist) */}
      {batches.length > 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Compliance report for batch:</label>
          <select
            data-testid="compliance-batch-picker"
            value={selectedBatch?.id || ''}
            onChange={(e) => setSelectedBatch(batches.find((b) => b.id === e.target.value))}
            className="flex-1 h-11 px-3 border border-slate-300 rounded-lg text-base"
          >
            {batches.map((b) => (
              <option key={b.id} value={b.id}>{b.batch_number || b.id} — {b.product_name || 'Unnamed'}</option>
            ))}
          </select>
        </div>
      )}

      {/* Chain of Custody Snapshot — primary, always visible */}
      <section
        data-testid="compliance-chain-of-custody-section"
        className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8"
      >
        <ChainOfCustodyTimeline
          events={events}
          corrections={corrections}
          title="Chain of Custody Snapshot"
          actionSlot={
            <Button
              onClick={() => hasBatch ? navigate(`/mvp/batches/${selectedBatch.id}`) : navigate('/mvp/chain-of-custody')}
              variant="outline"
              size="sm"
              className="h-10"
              data-testid="compliance-view-full-chain-of-custody"
            >
              {hasBatch ? 'View full batch timeline' : 'Open Chain of Custody'} <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          }
        />
      </section>

      {/* Compliance Overview (5 readiness pills) */}
      <section data-testid="compliance-overview-section">
        <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-3 sm:mb-4" style={{ fontFamily: 'Manrope' }}>
          Compliance Overview
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <ReadinessPill icon={Shield} label="Compliance Readiness" ready={report.compliance_summary.available} pendingText="Awaiting events" testId="readiness-compliance" />
          <ReadinessPill icon={ClipboardCheck} label="Audit Readiness" ready={report.compliance_summary.available} pendingText="Awaiting events" testId="readiness-audit" />
          <ReadinessPill icon={FileText} label="Documentation" ready={hasBatch} pendingText="Awaiting batch" testId="readiness-documentation" />
          <ReadinessPill icon={Workflow} label="Traceability" ready={report.traceability_summary.available} pendingText="Awaiting events" testId="readiness-traceability" />
          <ReadinessPill icon={Leaf} label="Sustainability" ready={report.sustainability_summary.available} pendingText="Awaiting records" testId="readiness-sustainability" />
        </div>
      </section>

      {/* Log Sustainability + Compliance — operator surfaces (auditor hidden) */}
      {(perms.canLogSustainabilityMetric || perms.canLogComplianceStandard) && (
        <div className="mt-4 sm:mt-6 lg:mt-8">
          {hasBatch ? (
            <BatchRecordingPanel
              batchId={selectedBatch.id}
              onLogged={async () => {
                try {
                  const [bRes, evRes, auRes] = await Promise.all([
                    axios.get(`${BACKEND_URL}/api/batches`).catch(() => ({ data: batches })),
                    axios.get(`${BACKEND_URL}/api/batches/${selectedBatch.id}/events`).catch(() => ({ data: events })),
                    axios.get(`${BACKEND_URL}/api/batches/${selectedBatch.id}/audit-trail`).catch(() => ({ data: audit })),
                  ]);
                  setBatches(bRes.data);
                  const updated = (bRes.data || []).find((b) => b.id === selectedBatch.id);
                  if (updated) setSelectedBatch(updated);
                  setEvents(evRes.data || []);
                  setAudit(auRes.data || []);
                } catch { /* noop */ }
              }}
            />
          ) : (
            <div
              data-testid="recording-panel-empty"
              className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 sm:p-8 text-center"
            >
              <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
                Register or select a batch to log a sustainability metric or compliance standard.
              </p>
              <Button
                onClick={() => navigate('/mvp/batches')}
                data-testid="recording-panel-empty-cta"
                className="mt-3 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                Open Batches
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Compliance Report Preview */}
      <section data-testid="compliance-report-preview" className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 sm:p-6 border-b border-slate-200">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Compliance Report Preview
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                A live preview of what a generated compliance report looks like
                for this batch.
              </p>
            </div>
          </div>

          {/* Three actions — ALWAYS visible */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3" data-testid="compliance-actions">
            <Button onClick={handleDownloadJSON} variant="outline" className="h-11" data-testid="download-json-button">
              <Download className="w-4 h-4 mr-2" /> Download JSON
            </Button>
            <Button onClick={handleDownloadPDF} variant="outline" className="h-11" data-testid="download-pdf-button">
              <FileText className="w-4 h-4 mr-2" /> Download PDF
            </Button>
            <Button onClick={handleOpenPublicQR} className="h-11 bg-slate-900 hover:bg-slate-800 text-white" data-testid="open-public-qr-button">
              <ExternalLink className="w-4 h-4 mr-2" /> Open Public QR Verification
            </Button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          <ReportSection title="Batch Identity" available={report.batch_identity.available} emptyText="No batch records available yet.">
            <ReportGrid>
              <Kv k="Batch ID" v={report.batch_identity.batch_id} />
              <Kv k="Batch Number" v={report.batch_identity.batch_number} />
              <Kv k="Product" v={report.batch_identity.product} />
              <Kv k="Species" v={report.batch_identity.species} />
              <Kv k="Origin" v={report.batch_identity.origin_name} />
              <Kv k="Catch/Harvest Location" v={report.batch_identity.catch_or_harvest_location} />
              <Kv k="Supplier" v={report.batch_identity.supplier} />
              <Kv k="Production Date" v={formatDate(report.batch_identity.production_date)} />
              <Kv k="Production Timestamp" v={formatTime(report.batch_identity.production_timestamp)} />
            </ReportGrid>
          </ReportSection>

          <ReportSection title="Traceability Summary" available={report.traceability_summary.available} emptyText="No traceability events recorded yet.">
            <ReportGrid>
              <Kv k="Status" v={report.traceability_summary.traceability_status} />
              <Kv k="Completeness" v={report.traceability_summary.traceability_completeness} />
              <Kv k="Chain-of-Custody" v={report.traceability_summary.chain_of_custody_status} />
              <Kv k="Verification" v={report.traceability_summary.verification_status} />
            </ReportGrid>
          </ReportSection>

          <ReportSection title="Chain of Custody Summary" available={report.chain_of_custody_summary.available} emptyText="No chain-of-custody events recorded yet.">
            <ReportGrid>
              <Kv k="Stages Completed" v={report.chain_of_custody_summary.stages_completed} />
              <Kv k="Manual Corrections" v={report.chain_of_custody_summary.manual_corrections_count} />
              <Kv k="Verification Outcome" v={report.chain_of_custody_summary.verification_outcome} />
            </ReportGrid>
            {hasBatch && (
              <button
                type="button"
                data-testid="view-full-chain-of-custody-link"
                onClick={() => navigate(`/mvp/batches/${selectedBatch.id}`)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
              >
                View full Chain of Custody timeline, gaps & manual corrections
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
          </ReportSection>

          <ReportSection title="Cold Chain Summary" available={report.cold_chain_summary.available} emptyText="No cold-chain activity recorded yet.">
            <ReportGrid>
              <Kv k="Sensor Readings" v={report.cold_chain_summary.total_sensor_readings} />
              <Kv k="Monitoring Sessions" v={report.cold_chain_summary.total_monitoring_sessions} />
              <Kv k="Threshold Breaches" v={report.cold_chain_summary.threshold_breaches} />
              <Kv k="Warning Events" v={report.cold_chain_summary.warning_events} />
              <Kv k="Critical Events" v={report.cold_chain_summary.critical_events} />
            </ReportGrid>
          </ReportSection>

          <ReportSection title="Sustainability Summary" available={report.sustainability_summary.available} emptyText="No sustainability records available yet.">
            <ReportGrid>
              <Kv k="Status" v={report.sustainability_summary.sustainability_status} />
              <Kv k="Total CO₂ (kg)" v={report.sustainability_summary.total_co2_kg} />
            </ReportGrid>
          </ReportSection>

          <ReportSection title="Compliance Summary" available={report.compliance_summary.available} emptyText="No compliance events recorded yet.">
            <ReportGrid>
              <Kv k="Documentation Completeness" v={report.compliance_summary.documentation_completeness} />
              <Kv k="Audit Readiness" v={report.compliance_summary.audit_readiness} />
              <Kv k="Outstanding Actions" v={report.compliance_summary.outstanding_actions} />
            </ReportGrid>
          </ReportSection>

          <ReportSection
            title="Manual Corrections"
            available={report.manual_corrections.available}
            emptyText="No manually-resolved events on this batch — every recorded checkpoint was live-captured."
          >
            <div
              className="mb-3 rounded-md border border-amber-200/70 bg-amber-50/60 px-3 py-2 text-xs sm:text-sm text-amber-900 leading-snug"
              data-testid="manual-corrections-governance-note"
            >
              <p className="font-semibold uppercase tracking-wider text-[10px] sm:text-[11px] text-amber-700 mb-0.5">
                Verified Operator Resolutions
              </p>
              <p>{report.manual_corrections.description}</p>
              <p className="mt-1 text-amber-800/90">
                <span className="font-semibold">{report.manual_corrections.live_vs_corrected.live_events}</span> live-captured events
                {' · '}
                <span className="font-semibold">{report.manual_corrections.live_vs_corrected.manually_resolved}</span> verified operator resolution{report.manual_corrections.live_vs_corrected.manually_resolved === 1 ? '' : 's'}
              </p>
            </div>
            <ul className="space-y-3" data-testid="manual-corrections-list">
              {report.manual_corrections.entries.map((mc) => (
                <li
                  key={mc.correction_id}
                  className="bg-amber-50/40 rounded-lg p-3 border border-amber-200"
                  data-testid={`manual-correction-${mc.correction_id}`}
                >
                  <p className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 border border-amber-300 rounded-full text-[10px] font-semibold text-amber-800 uppercase tracking-wider">
                      Manually Resolved
                    </span>
                    {mc.missing_stage}
                    <span className="text-slate-500 font-normal text-xs">({mc.correction_id})</span>
                  </p>
                  <p className="text-xs text-slate-700 mt-2"><strong>Reason:</strong> {mc.reason}</p>
                  <p className="text-xs text-slate-700"><strong>Corrective action:</strong> {mc.corrective_action}</p>
                  {mc.evidence_label && (
                    <p className="text-xs text-slate-700"><strong>Evidence:</strong> {mc.evidence_label}</p>
                  )}
                  <p className="text-[11px] text-amber-800/80 mt-1">
                    Resolved by <span className="font-semibold">{mc.resolved_by || 'operator'}</span>
                    {mc.resolved_by_role ? ` (${mc.resolved_by_role})` : ''}
                    {' · '}{new Date(mc.timestamp).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          </ReportSection>

          <ReportSection title="Corrective Actions" available={report.corrective_actions.available} emptyText="No corrective actions logged.">
            <ul className="space-y-3">
              {report.corrective_actions.manual_corrections.map((mc) => (
                <li key={mc.correction_id} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-sm font-semibold text-slate-900">
                    {mc.missing_stage} <span className="text-slate-500 font-normal">({mc.correction_id})</span>
                  </p>
                  <p className="text-xs text-slate-700 mt-1"><strong>Reason:</strong> {mc.reason}</p>
                  <p className="text-xs text-slate-700"><strong>Action:</strong> {mc.corrective_action}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{mc.user} · {new Date(mc.timestamp).toLocaleString()} · {mc.status}</p>
                </li>
              ))}
            </ul>
          </ReportSection>

          <ReportSection title="Audit Trail Summary" available={report.audit_trail_summary.available} emptyText="No audit events recorded yet.">
            <ReportGrid>
              <Kv k="Total Audit Events" v={report.audit_trail_summary.total_audit_events} />
              <Kv k="Manual Corrections" v={report.audit_trail_summary.manual_corrections_count} />
              <Kv k="Verification Actions" v={report.audit_trail_summary.verification_actions} />
              <Kv k="System-Generated Events" v={report.audit_trail_summary.system_generated_events} />
            </ReportGrid>
          </ReportSection>
        </div>
      </section>

      {/* Audit Trail (from operator checkpoint actions) */}
      <section data-testid="compliance-audit-trail-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3 sm:mb-4">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            Audit Trail
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedBatch && <ChainStatusBadge batchId={selectedBatch.id} />}
            {hasBatch && audit.length > 0 && (
              <span className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-slate-500">
                {audit.length} event{audit.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
        <AuditTrailList entries={audit} />
      </section>

      {/* Customer QR Verification */}
      <section data-testid="customer-qr-section" className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
        <div className="mb-4">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            Customer QR Verification
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">
            A preview of the customer-facing transparency experience generated
            from this batch&apos;s verified records.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="flex flex-col items-center justify-center bg-slate-50 rounded-xl p-4 sm:p-6 border border-slate-200">
            {qrUrl ? (
              <>
                <div className="bg-white p-3 rounded-lg border border-slate-200">
                  <QRCodeSVG value={qrUrl} size={140} level="M" />
                </div>
                <p className="text-[11px] text-slate-500 break-all mt-3 text-center max-w-full">{qrUrl}</p>
                <Button onClick={handleOpenPublicQR} className="mt-3 w-full h-10 bg-slate-900 hover:bg-slate-800 text-white" data-testid="open-public-verification-button">
                  <ExternalLink className="w-4 h-4 mr-2" /> Open Public Verification Page
                </Button>
              </>
            ) : (
              <div className="text-center py-6">
                <Inbox className="w-7 h-7 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">QR appears once a batch is registered.</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-3">
            <ReportGrid>
              <Kv k="Product" v={report.batch_identity.product} />
              <Kv k="Species" v={report.batch_identity.species} />
              <Kv k="Origin" v={report.batch_identity.origin_name} />
              <Kv k="Catch/Harvest Location" v={report.batch_identity.catch_or_harvest_location} />
              <Kv k="Production Date" v={formatDate(report.batch_identity.production_date)} />
              <Kv k="Storage Status" v={report.batch_identity.current_status} />
              <Kv k="Quality Grade" v={null} placeholder="Awaiting QC records" />
            </ReportGrid>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-slate-100">
              <SummaryBlock
                icon={Leaf}
                title="Sustainability Summary"
                ready={report.sustainability_summary.available}
                content={report.sustainability_summary.available
                  ? `Total CO₂: ${report.sustainability_summary.total_co2_kg} kg`
                  : 'No sustainability records available yet.'}
              />
              <SummaryBlock
                icon={Shield}
                title="Compliance Summary"
                ready={report.compliance_summary.available}
                content={report.compliance_summary.available
                  ? report.compliance_summary.audit_readiness
                  : 'No compliance events recorded yet.'}
              />
            </div>
          </div>
        </div>
      </section>

      <p className="text-[11px] text-slate-500 leading-relaxed">
        <strong className="text-slate-600">Note:</strong> Reports are generated
        from operational data recorded within PureSustain. Synthetic or
        placeholder values are intentionally not displayed. Generated reports
        support audit preparation and stakeholder transparency, but do not
        replace accredited certification.
      </p>
    </div>
  );
};

/* ----------------------- subcomponents ----------------------- */

const ReadinessPill = ({ icon: Icon, label, ready, pendingText, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-xl p-3 sm:p-4 border ${ready ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
  >
    <div className="flex items-start gap-2 sm:gap-3">
      <div className={`p-2 rounded-lg flex-shrink-0 ${ready ? 'bg-emerald-100' : 'bg-slate-200'}`}>
        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${ready ? 'text-emerald-700' : 'text-slate-500'}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-600">{label}</p>
        {ready ? (
          <p className="text-sm sm:text-base font-semibold text-emerald-700 mt-0.5 flex items-center gap-1">
            <CheckCircle className="w-4 h-4" /> Ready
          </p>
        ) : (
          <p className="text-xs sm:text-sm font-medium text-slate-500 mt-0.5 leading-snug">{pendingText}</p>
        )}
      </div>
    </div>
  </div>
);

const ReportSection = ({ title, available, emptyText, children }) => (
  <div>
    <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-2 sm:mb-3 uppercase tracking-wider">
      {title}
    </h3>
    {available ? children : (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3 sm:p-4">
        <p className="text-xs sm:text-sm text-slate-500">{emptyText}</p>
      </div>
    )}
  </div>
);

const ReportGrid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">{children}</div>
);

const Kv = ({ k, v, placeholder = '—' }) => {
  const isEmpty = v === null || v === undefined || v === '';
  return (
    <div className="flex flex-col sm:flex-row sm:gap-2 text-xs sm:text-sm">
      <span className="text-slate-500 sm:w-44 flex-shrink-0">{k}:</span>
      <span className={isEmpty ? 'text-slate-400 italic' : 'text-slate-800 break-words'}>
        {isEmpty ? placeholder : String(v)}
      </span>
    </div>
  );
};

const SummaryBlock = ({ icon: Icon, title, ready, content }) => (
  <div className={`rounded-lg p-3 border ${ready ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${ready ? 'text-emerald-600' : 'text-slate-400'}`} />
      <p className="text-xs uppercase tracking-wider text-slate-600">{title}</p>
    </div>
    <p className={`text-xs sm:text-sm ${ready ? 'text-slate-800' : 'text-slate-500 italic'}`}>{content}</p>
  </div>
);

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-NO', { year: 'numeric', month: 'short', day: 'numeric' }) : null);
const formatTime = (d) => (d ? new Date(d).toLocaleString() : null);

export default Compliance;
